import { styled } from "styled-components";
import { createEditableProps } from "@ludens-reklame/rubics-v3-react/util/fns";
import {
  ButtonAction,
  ButtonActionProps,
} from "../../internal-components/Button.js";
import { Constrainer } from "../../internal-components/Layout.js";
import { cssSpacing, surfaceColorMap } from "../../util/styles.js";
import {
  HorizontalSpacer,
  VerticalSpacer,
} from "../../internal-components/Layout.js";
import { RichText } from "../../internal-components/RichText.js";
import { mobileBreakPointCSS } from "../../defaults/theme.js";
import type { BackgroundColor } from "../../types/theme.js";

export interface FactBoxItem {
  text?: string;
  actions?: Partial<ButtonActionProps>[];
}

export interface Props {
  list?: FactBoxItem[];
  backgroundColor?: BackgroundColor;
}

export const FactBox: React.FC<Props> = ({ list = [], backgroundColor = "surface2" }) => {
  return (
    <VerticalSpacer>
      <HorizontalSpacer>
        <FactBoxConstrainer multiple={list.length > 1}>
          {list.map(({ text, actions = [] }, i) => (
            <FactBoxCard key={i} multiple={list.length > 1} $bg={backgroundColor}>
              {text && (
                <RichText
                  dangerouslySetInnerHTML={{ __html: text }}
                  className="b1"
                  {...createEditableProps("html", `list.${i}.text`)}
                />
              )}
              <FactBoxActions>
                {actions
                  .filter((x) => x.link)
                  .map(({ link, variant, animation }, iii) => (
                    <ButtonAction
                      key={iii}
                      link={link!}
                      variant={variant}
                      animation={animation}
                    />
                  ))}
              </FactBoxActions>
            </FactBoxCard>
          ))}
        </FactBoxConstrainer>
      </HorizontalSpacer>
    </VerticalSpacer>
  );
};

const FactBoxConstrainer = styled(Constrainer)<{ multiple?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.m};

  @media (min-width: ${mobileBreakPointCSS}) {
    align-items: stretch;
    justify-content: ${({ multiple }) => (multiple ? "initial" : "center")};
    flex-direction: row;
  }
`;

const FactBoxCard = styled.div<{ multiple?: boolean; $bg: BackgroundColor }>`
  background-color: ${({ $bg }) => surfaceColorMap[$bg]};
  color: ${({ $bg }) => ($bg === "surface3" ? "#fff" : "var(--swatch2)")};
  svg path { fill: currentColor; stroke: currentColor; }
  box-sizing: border-box;
  flex: 0.5;
  padding: ${cssSpacing("l", "l")};
  max-width: 600px;
  width: 100%;

  border-radius: ${(props) => props.theme.borderRadius};

  @media (min-width: ${mobileBreakPointCSS}) {
    padding: ${cssSpacing("xl", "xl")};
  }
`;

const FactBoxActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.m};
  margin-top: ${({ theme }) => theme.spacing.m};

  > a {
    width: 100%;
  }

  @media (min-width: ${mobileBreakPointCSS}) {
    > a {
      width: auto;
    }
  }
`;
